import { PrefixGroupConfig, TemplateDataType } from '../../../types';

// Reserved: the implicit group for fields with no explicit `group` key.
// Always a valid pairing *base*, never a variant (see findVariantGroups).
const DEFAULT_GROUP = 'default';

export interface PrefixGroupPair {
  prefix: string;
  configFlag: string;
  baseGroup: string;
  variantGroup: string;
  // Declaration-order field keys of variantGroup.
  variantFieldKeys: string[];
  toggleLabels?: PrefixGroupConfig['toggleLabels'];
  copyContent?: PrefixGroupConfig['copyContent'];
}

// Env-split fail-loud reporting: dev/test throws (loud CI/local feedback),
// prod logs and lets the caller skip the malformed pair (renders flat)
// instead of crashing the whole Localisation tab.
const reportPrefixGroupIssue = (message: string): void => {
  if (process.env.NODE_ENV !== 'production') {
    throw new Error(message);
  }
  // eslint-disable-next-line no-console
  console.error(message);
};

// Groups a section's fields by their (possibly implicit) group, preserving
// first-seen declaration order for both the groups and their field keys.
const groupFieldsInDeclarationOrder = (
  section: TemplateDataType[string],
): { groupOrder: string[]; groupFieldKeys: Map<string, string[]> } => {
  const groupOrder: string[] = [];
  const groupFieldKeys = new Map<string, string[]>();

  Object.keys(section).forEach(fieldKey => {
    const groupName = section[fieldKey].group ?? DEFAULT_GROUP;
    if (!groupFieldKeys.has(groupName)) {
      groupOrder.push(groupName);
      groupFieldKeys.set(groupName, []);
    }
    groupFieldKeys.get(groupName)?.push(fieldKey);
  });

  return { groupOrder, groupFieldKeys };
};

// A group qualifies as a "variant group" for `prefix` iff EVERY field key
// belonging to that group starts with `prefix`. Detection is group-level,
// not field-key-level: stripping the prefix from a variant field key does
// not always match a base field key by name (e.g. `family_plan_checkbox`
// has no correspondent named `checkbox`; its base pair is `checkboxLabel`).
// `default` is never eligible — it is reserved as a pairing base only.
export const findVariantGroups = (
  section: TemplateDataType[string],
  prefix: string,
): string[] => {
  const { groupOrder, groupFieldKeys } = groupFieldsInDeclarationOrder(section);

  return groupOrder.filter(groupName => {
    if (groupName === DEFAULT_GROUP) {
      return false;
    }
    const fieldKeys = groupFieldKeys.get(groupName) ?? [];
    return fieldKeys.every(key => key.startsWith(prefix));
  });
};

// Pairs each detected variant group with the nearest preceding non-variant
// group in the section's first-seen field declaration order. Reuses
// findVariantGroups per configured prefix so rendering (this function) and
// flag-gating (filterTemplateByConfig) can never disagree.
export const pairPrefixGroups = (
  section: TemplateDataType[string],
  prefixGroups: PrefixGroupConfig[],
): PrefixGroupPair[] => {
  if (!prefixGroups.length) {
    return [];
  }

  const { groupOrder, groupFieldKeys } = groupFieldsInDeclarationOrder(section);
  const variantGroupsByConfig = prefixGroups.map(config => ({
    config,
    variantGroups: new Set(findVariantGroups(section, config.prefix)),
  }));

  const pairs: PrefixGroupPair[] = [];
  const claimedBaseGroups = new Set<string>();
  let lastNonVariantGroup: string | undefined;

  groupOrder.forEach(groupName => {
    const matches = variantGroupsByConfig.filter(({ variantGroups }) =>
      variantGroups.has(groupName),
    );

    if (matches.length > 1) {
      reportPrefixGroupIssue(
        `[prefixGroups] Group "${groupName}" matches more than one configured prefix (${matches
          .map(({ config }) => config.prefix)
          .join(', ')}); excluding it from pairing entirely.`,
      );
      return;
    }

    if (matches.length === 0) {
      lastNonVariantGroup = groupName;
      return;
    }

    const { config } = matches[0];

    if (!lastNonVariantGroup) {
      reportPrefixGroupIssue(
        `[prefixGroups] Variant group "${groupName}" has no preceding base group to pair with.`,
      );
      return;
    }

    if (claimedBaseGroups.has(lastNonVariantGroup)) {
      reportPrefixGroupIssue(
        `[prefixGroups] Base group "${lastNonVariantGroup}" is already paired; variant group "${groupName}" cannot claim it again.`,
      );
      return;
    }

    claimedBaseGroups.add(lastNonVariantGroup);
    pairs.push({
      prefix: config.prefix,
      configFlag: config.configFlag,
      baseGroup: lastNonVariantGroup,
      variantGroup: groupName,
      variantFieldKeys: groupFieldKeys.get(groupName) ?? [],
      toggleLabels: config.toggleLabels,
      copyContent: config.copyContent,
    });
  });

  return pairs;
};

// Builds the value patch for a "copy content" click: for every variant field
// of every pair, resolve its source counterpart by stripping the pair's prefix
// (and re-prefixing with `source` when copying from another variant) and take
// that field's current value. Relies on twin keys being exact prefix mirrors
// of their source keys; keys with no counterpart are skipped. Arrays (richText
// content) are deep-cloned so the two tabs never share a mutable reference.
export const buildCopiedValues = (
  values: Record<string, unknown>,
  pairs: PrefixGroupPair[],
  source: 'base' | string,
): Record<string, unknown> => {
  const copied: Record<string, unknown> = {};

  pairs.forEach(pair => {
    pair.variantFieldKeys.forEach(variantKey => {
      const strippedKey = variantKey.slice(pair.prefix.length);
      const sourceKey =
        source === 'base' ? strippedKey : `${source}${strippedKey}`;

      if (!(sourceKey in values)) {
        return;
      }

      const value = values[sourceKey];
      copied[variantKey] = Array.isArray(value)
        ? structuredClone(value)
        : value;
    });
  });

  return copied;
};
